import React, { useState, useEffect, useCallback, useRef } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useRBAC } from '../../contexts/RBACContext';
import { logAuditAction } from '../../utils/auditLogger';

const STATUSES = [
  { value: 'open', label: 'Open', color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/30', icon: 'Circle' },
  { value: 'matched', label: 'Matched', color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/30', icon: 'Link2' },
  { value: 'returned', label: 'Returned', color: 'text-green-600 bg-green-50 dark:bg-green-900/30', icon: 'CheckCircle2' },
  { value: 'closed', label: 'Closed', color: 'text-slate-500 bg-slate-100 dark:bg-slate-800', icon: 'XCircle' },
];

const CATEGORIES = [
  { value: 'electronics', label: 'Electronics', icon: 'Smartphone' },
  { value: 'clothing', label: 'Clothing & Accessories', icon: 'Shirt' },
  { value: 'documents', label: 'Documents & ID', icon: 'FileText' },
  { value: 'keys', label: 'Keys', icon: 'Key' },
  { value: 'bags', label: 'Bags & Luggage', icon: 'Briefcase' },
  { value: 'jewelry', label: 'Jewelry & Watches', icon: 'Watch' },
  { value: 'books', label: 'Books & Stationery', icon: 'BookOpen' },
  { value: 'sports', label: 'Sports Equipment', icon: 'Dumbbell' },
  { value: 'other', label: 'Other', icon: 'Package' },
];

const BUILDINGS = [
  'Building A', 'Building B', 'Building C', 'Building D',
  'Library', 'Cafeteria', 'Sports Complex', 'Administration',
  'Lecture Hall', 'Laboratory', 'Dormitory', 'Parking Area', 'Other',
];

const getStatusStyle = (status) => STATUSES?.find(s => s?.value === status)?.color || 'text-slate-500 bg-slate-100';
const getCategoryLabel = (cat) => CATEGORIES?.find(c => c?.value === cat)?.label || cat;
const getCategoryIcon = (cat) => CATEGORIES?.find(c => c?.value === cat)?.icon || 'Package';

const EMPTY_FORM = {
  item_type: 'lost',
  item_name: '',
  description: '',
  location: '',
  building: '',
  category: 'other',
};

export default function LostFound() {
  const { user } = useAuth();
  const { notifyAdmins, sendNotification } = useNotifications();
  const { isAdmin, isViewer } = useRBAC();
  const isAdminOrViewer = isAdmin || isViewer;
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [selectedItem, setSelectedItem] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [adminStatus, setAdminStatus] = useState('');
  const [matchItemId, setMatchItemId] = useState('');
  const [updatingItem, setUpdatingItem] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [reportType, setReportType] = useState(null); // 'lost' | 'found' — for quick button flow

  const [form, setForm] = useState(EMPTY_FORM);

  // ── Fetch items ──────────────────────────────────────────────────────────────
  const fetchItems = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      let query = supabase
        ?.from('lost_found_items')
        ?.select('*, reporter:reported_by(full_name, email)')
        ?.order('created_at', { ascending: false });
      if (!isAdminOrViewer) {
        query = query?.eq('reported_by', user?.id);
      }
      const { data, error } = await query;
      if (!error) setItems(data || []);
    } catch (err) {
      console.error('fetchItems error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isAdminOrViewer]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // ── Realtime subscription ────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      ?.channel('lost_found_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'lost_found_items' }, () => {
        fetchItems();
      })
      ?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [user?.id, fetchItems]);

  // ── Auto-match suggestions ───────────────────────────────────────────────────
  const findMatches = useCallback((currentForm, allItems) => {
    if (!currentForm?.item_name?.trim()) return [];
    const oppositeType = currentForm?.item_type === 'lost' ? 'found' : 'lost';
    const keywords = currentForm?.item_name?.toLowerCase()?.split(' ')?.filter(w => w?.length > 2);
    return allItems?.filter(item => {
      if (item?.item_type !== oppositeType) return false;
      if (item?.status !== 'open') return false;
      const nameMatch = keywords?.some(kw =>
        item?.item_name?.toLowerCase()?.includes(kw) ||
        item?.description?.toLowerCase()?.includes(kw)
      );
      const catMatch = currentForm?.category && currentForm?.category !== 'other' &&
        item?.category === currentForm?.category;
      const buildingMatch = currentForm?.building && item?.building === currentForm?.building;
      return nameMatch || catMatch || buildingMatch;
    })?.slice(0, 5);
  }, []);

  useEffect(() => {
    if (activeTab === 'new' && form?.item_name?.length > 2) {
      const suggestions = findMatches(form, items);
      setMatchSuggestions(suggestions);
    } else {
      setMatchSuggestions([]);
    }
  }, [form?.item_name, form?.category, form?.building, form?.item_type, items, activeTab, findMatches]);

  // ── Photo handling ───────────────────────────────────────────────────────────
  const handlePhotoChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.size > 5 * 1024 * 1024) {
      setFormError('Photo must be under 5MB.');
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader?.result);
    reader?.readAsDataURL(file);
    setFormError('');
  };

  const uploadPhoto = async (itemId) => {
    if (!photoFile) return null;
    setUploadingPhoto(true);
    try {
      const ext = photoFile?.name?.split('.')?.pop();
      const path = `${user?.id}/${itemId}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase?.storage
        ?.from('lost-found-photos')
        ?.upload(path, photoFile, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data } = supabase?.storage?.from('lost-found-photos')?.getPublicUrl(path);
      return data?.publicUrl || null;
    } catch (err) {
      console.error('Photo upload error:', err);
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ── Submit report ────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.item_name?.trim()) {
      setFormError('Item name is required.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      // Insert item first to get ID
      const { data: newItem, error: insertError } = await supabase
        ?.from('lost_found_items')
        ?.insert({
          item_type: form?.item_type,
          item_name: form?.item_name?.trim(),
          description: form?.description?.trim() || null,
          location: form?.location?.trim() || null,
          building: form?.building || null,
          category: form?.category || 'other',
          reported_by: user?.id,
          status: 'open',
          date_reported: new Date()?.toISOString()?.split('T')?.[0],
        })
        ?.select()
        ?.single();
      if (insertError) throw insertError;

      // Upload photo if provided
      let photoUrl = null;
      if (photoFile) {
        photoUrl = await uploadPhoto(newItem?.id);
        if (photoUrl) {
          await supabase?.from('lost_found_items')?.update({ photo_url: photoUrl })?.eq('id', newItem?.id);
        }
      }

      // Notify admins
      await notifyAdmins({
        title: `${form?.item_type === 'lost' ? 'Lost' : 'Found'} Item Reported`,
        message: `"${form?.item_name}" reported as ${form?.item_type}${form?.building ? ` at ${form?.building}` : ''}${form?.location ? ` — ${form?.location}` : ''}`,
        type: 'info',
        relatedId: newItem?.id,
        relatedTable: 'lost_found_items',
      });

      // Check for auto-matches and notify
      const matches = findMatches(form, items);
      if (matches?.length > 0) {
        await sendNotification({
          userId: user?.id,
          title: '🎯 Potential Match Found!',
          message: `We found ${matches?.length} potential match${matches?.length > 1 ? 'es' : ''} for your ${form?.item_type} item "${form?.item_name}". Check the matches tab!`,
          type: 'success',
          relatedId: newItem?.id,
          relatedTable: 'lost_found_items',
        });
      }

      setFormSuccess('Your report has been submitted successfully!' + (matches?.length > 0 ? ` ${matches?.length} potential match${matches?.length > 1 ? 'es' : ''} found!` : ' We will notify you if a match is found.'));
      setForm(EMPTY_FORM);
      setPhotoFile(null);
      setPhotoPreview(null);
      setReportType(null);
      fetchItems();
      setTimeout(() => { setFormSuccess(''); setActiveTab('list'); }, 4000);
    } catch (err) {
      setFormError(err?.message || 'Failed to submit report.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Admin update ─────────────────────────────────────────────────────────────
  const handleAdminUpdate = async () => {
    if (!selectedItem) return;
    setUpdatingItem(true);
    try {
      const updates = {};
      if (adminStatus) updates.status = adminStatus;
      if (adminNotes !== undefined) updates.admin_notes = adminNotes;
      if (matchItemId) updates.matched_item_id = matchItemId;
      updates.updated_at = new Date()?.toISOString();

      const { error } = await supabase?.from('lost_found_items')?.update(updates)?.eq('id', selectedItem?.id);
      if (error) throw error;

      // If marking as matched, also update the matched item
      if (adminStatus === 'matched' && matchItemId) {
        await supabase?.from('lost_found_items')?.update({
          status: 'matched',
          matched_item_id: selectedItem?.id,
          admin_notes: `Matched with item: ${selectedItem?.item_name}`,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', matchItemId);

        // Notify the reporter of the matched item
        const matchedItem = items?.find(i => i?.id === matchItemId);
        if (matchedItem?.reported_by) {
          await sendNotification({
            userId: matchedItem?.reported_by,
            title: '🎯 Match Found for Your Item!',
            message: `Your ${matchedItem?.item_type} item "${matchedItem?.item_name}" has been matched! Please contact the administration.`,
            type: 'success',
            relatedId: matchedItem?.id,
            relatedTable: 'lost_found_items',
          });
        }
      }

      // Notify the reporter of status change
      if (selectedItem?.reported_by && adminStatus) {
        const statusLabel = STATUSES?.find(s => s?.value === adminStatus)?.label || adminStatus;
        await sendNotification({
          userId: selectedItem?.reported_by,
          title: 'Lost & Found Update',
          message: `Your ${selectedItem?.item_type} item "${selectedItem?.item_name}" status has been updated to: ${statusLabel}${adminNotes ? `. Note: ${adminNotes}` : ''}`,
          type: adminStatus === 'matched' || adminStatus === 'returned' ? 'success' : 'info',
          relatedId: selectedItem?.id,
          relatedTable: 'lost_found_items',
        });
      }

      await logAuditAction({
        action: 'UPDATE',
        module: 'lost_found',
        recordId: selectedItem?.id,
        recordSummary: `Updated Lost & Found item: "${selectedItem?.item_name}" → status: ${adminStatus || selectedItem?.status}`,
        user,
      });

      setAdminNotes(''); setAdminStatus(''); setMatchItemId('');
      setSelectedItem(null);
      fetchItems();
    } catch (err) {
      console.error('Admin update error:', err);
    } finally {
      setUpdatingItem(false);
    }
  };

  // ── Filtered items ───────────────────────────────────────────────────────────
  const filtered = items?.filter(item => {
    const matchType = filterType === 'all' || item?.item_type === filterType;
    const matchStatus = filterStatus === 'all' || item?.status === filterStatus;
    const matchCat = filterCategory === 'all' || item?.category === filterCategory;
    const matchSearch = !searchQuery ||
      item?.item_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.building?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchType && matchStatus && matchCat && matchSearch;
  });

  // ── Stats ────────────────────────────────────────────────────────────────────
  const stats = {
    lost: items?.filter(i => i?.item_type === 'lost' && i?.status === 'open')?.length || 0,
    found: items?.filter(i => i?.item_type === 'found' && i?.status === 'open')?.length || 0,
    matched: items?.filter(i => i?.status === 'matched')?.length || 0,
    returned: items?.filter(i => i?.status === 'returned')?.length || 0,
  };

  // ── Recent activity (last 5 updated items) ───────────────────────────────────
  const recentActivity = [...items]
    ?.sort((a, b) => new Date(b?.updated_at || b?.created_at) - new Date(a?.updated_at || a?.created_at))
    ?.slice(0, 5);

  // ── Potential matches for admin ──────────────────────────────────────────────
  const getPotentialMatches = (item) => {
    if (!item) return [];
    const oppositeType = item?.item_type === 'lost' ? 'found' : 'lost';
    const keywords = item?.item_name?.toLowerCase()?.split(' ')?.filter(w => w?.length > 2);
    return items?.filter(i => {
      if (i?.id === item?.id) return false;
      if (i?.item_type !== oppositeType) return false;
      if (i?.status !== 'open') return false;
      const nameMatch = keywords?.some(kw =>
        i?.item_name?.toLowerCase()?.includes(kw) ||
        i?.description?.toLowerCase()?.includes(kw)
      );
      const catMatch = item?.category && item?.category !== 'other' && i?.category === item?.category;
      const buildingMatch = item?.building && i?.building === item?.building;
      return nameMatch || catMatch || buildingMatch;
    });
  };

  // ── Quick report button handler ───────────────────────────────────────────────
  const handleQuickReport = (type) => {
    setReportType(type);
    setForm({ ...EMPTY_FORM, item_type: type });
    setActiveTab('new');
  };

  const tabs = [
    { id: 'list', label: 'All Reports', icon: 'List' },
    { id: 'activity', label: 'Recent Activity', icon: 'Activity' },
    ...(!isAdminOrViewer ? [{ id: 'new', label: 'Report Item', icon: 'Plus' }] : []),
    ...(isAdmin ? [{ id: 'matches', label: 'Matches', icon: 'Link2' }] : []),
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Lost & Found
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Report lost or found items on campus</p>
          </div>
          {/* Quick report buttons for guests/viewers */}
          {!isAdmin && (
            <div className="flex gap-2">
              <button
                onClick={() => handleQuickReport('lost')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors shadow-sm"
              >
                <Icon name="SearchX" size={15} />
                Report Lost Item
              </button>
              <button
                onClick={() => handleQuickReport('found')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-colors shadow-sm"
              >
                <Icon name="PackageSearch" size={15} />
                Report Found Item
              </button>
            </div>
          )}
          {isAdmin && (
            <div className="flex gap-2">
              <button
                onClick={() => { setForm({ ...EMPTY_FORM, item_type: 'lost' }); setActiveTab('new'); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors shadow-sm"
              >
                <Icon name="SearchX" size={15} />
                Report Lost
              </button>
              <button
                onClick={() => { setForm({ ...EMPTY_FORM, item_type: 'found' }); setActiveTab('new'); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-colors shadow-sm"
              >
                <Icon name="PackageSearch" size={15} />
                Report Found
              </button>
            </div>
          )}
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Lost (Open)', value: stats?.lost, icon: 'SearchX', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20' },
            { label: 'Found (Open)', value: stats?.found, icon: 'PackageSearch', color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/20' },
            { label: 'Matched', value: stats?.matched, icon: 'Link2', color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-900/20' },
            { label: 'Returned', value: stats?.returned, icon: 'CheckCircle2', color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20' },
          ]?.map(s => (
            <div key={s?.label} className="bg-card border border-border rounded-xl p-4">
              <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${s?.bg} mb-3`}>
                <Icon name={s?.icon} size={18} className={s?.color} />
              </div>
              <div className="text-2xl font-bold text-foreground">{s?.value}</div>
              <div className="text-xs text-muted-foreground font-medium mt-0.5">{s?.label}</div>
            </div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 bg-muted/30 rounded-lg p-1 w-fit flex-wrap">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* ── Report Form ── */}
        {activeTab === 'new' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-foreground mb-6">
              {form?.item_type === 'lost' ? '🔍 Report a Lost Item' : '📦 Report a Found Item'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Report Type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Report Type *</label>
                <div className="flex gap-3">
                  {[
                    { value: 'lost', label: 'I Lost Something', icon: 'SearchX', activeClass: 'border-red-500 bg-red-50 text-red-600 dark:bg-red-900/20' },
                    { value: 'found', label: 'I Found Something', icon: 'PackageSearch', activeClass: 'border-green-500 bg-green-50 text-green-600 dark:bg-green-900/20' },
                  ]?.map(t => (
                    <button
                      key={t?.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, item_type: t?.value }))}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                        form?.item_type === t?.value ? t?.activeClass : 'border-border text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      <Icon name={t?.icon} size={16} />
                      {t?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Category *</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {CATEGORIES?.map(cat => (
                    <button
                      key={cat?.value}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, category: cat?.value }))}
                      className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                        form?.category === cat?.value
                          ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                      }`}
                    >
                      <Icon name={cat?.icon} size={16} />
                      <span className="text-center leading-tight">{cat?.label?.split(' ')?.[0]}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Item Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Item Name *</label>
                <input
                  type="text"
                  value={form?.item_name}
                  onChange={e => setForm(f => ({ ...f, item_name: e?.target?.value }))}
                  placeholder="e.g. Blue backpack, iPhone 14, Student ID card"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {/* Match suggestions */}
                {matchSuggestions?.length > 0 && (
                  <div className="mt-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 text-xs font-semibold mb-2">
                      <Icon name="Sparkles" size={13} />
                      {matchSuggestions?.length} potential match{matchSuggestions?.length > 1 ? 'es' : ''} already reported!
                    </div>
                    {matchSuggestions?.slice(0, 2)?.map(m => (
                      <div key={m?.id} className="text-xs text-amber-600 dark:text-amber-300 flex items-center gap-1.5 mb-1">
                        <Icon name={m?.item_type === 'found' ? 'PackageSearch' : 'SearchX'} size={11} />
                        <span className="font-medium">{m?.item_name}</span>
                        {m?.building && <span className="text-amber-500">— {m?.building}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Building + Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Building</label>
                  <select
                    value={form?.building}
                    onChange={e => setForm(f => ({ ...f, building: e?.target?.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="">Select building...</option>
                    {BUILDINGS?.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {form?.item_type === 'lost' ? 'Last Seen Location' : 'Found Location'}
                  </label>
                  <input
                    type="text"
                    value={form?.location}
                    onChange={e => setForm(f => ({ ...f, location: e?.target?.value }))}
                    placeholder={form?.item_type === 'lost' ? 'e.g. Room 201, Lobby...' : 'e.g. Near entrance, Cafeteria...'}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                <textarea
                  value={form?.description}
                  onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
                  rows={3}
                  placeholder="Provide details: color, brand, distinguishing features, serial number..."
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Item Photo (optional)</label>
                <div
                  onClick={() => fileInputRef?.current?.click()}
                  className="border-2 border-dashed border-border rounded-xl p-4 cursor-pointer hover:border-primary/50 transition-colors text-center"
                >
                  {photoPreview ? (
                    <div className="relative inline-block">
                      <img src={photoPreview} alt="Item preview" className="max-h-40 rounded-lg mx-auto object-cover" />
                      <button
                        type="button"
                        onClick={e => { e?.stopPropagation(); setPhotoFile(null); setPhotoPreview(null); }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Icon name="Camera" size={24} className="opacity-50" />
                      <span className="text-sm">Click to upload a photo</span>
                      <span className="text-xs">JPG, PNG, WebP — max 5MB</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>

              {formError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {formError}
                </div>
              )}
              {formSuccess && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 text-sm">
                  <Icon name="CheckCircle" size={14} />
                  {formSuccess}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting || uploadingPhoto}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {(submitting || uploadingPhoto) ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Send" size={14} />}
                  {submitting ? 'Submitting...' : uploadingPhoto ? 'Uploading photo...' : 'Submit Report'}
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveTab('list'); setForm(EMPTY_FORM); setPhotoFile(null); setPhotoPreview(null); setFormError(''); setFormSuccess(''); }}
                  className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── All Reports List ── */}
        {activeTab === 'list' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  placeholder="Search items..."
                  className="pl-8 pr-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-48"
                />
              </div>
              <select value={filterType} onChange={e => setFilterType(e?.target?.value)} className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none">
                <option value="all">All Types</option>
                <option value="lost">Lost</option>
                <option value="found">Found</option>
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none">
                <option value="all">All Statuses</option>
                {STATUSES?.map(s => <option key={s?.value} value={s?.value}>{s?.label}</option>)}
              </select>
              <select value={filterCategory} onChange={e => setFilterCategory(e?.target?.value)} className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none">
                <option value="all">All Categories</option>
                {CATEGORIES?.map(c => <option key={c?.value} value={c?.value}>{c?.label}</option>)}
              </select>
              {(filterType !== 'all' || filterStatus !== 'all' || filterCategory !== 'all' || searchQuery) && (
                <button
                  onClick={() => { setFilterType('all'); setFilterStatus('all'); setFilterCategory('all'); setSearchQuery(''); }}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={12} />
                  Clear
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
              </div>
            ) : filtered?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="PackageSearch" size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-medium">No reports found</p>
                <p className="text-sm mt-1">
                  {!isAdminOrViewer ? 'Use the buttons above to report a lost or found item.' : 'No items match your filters.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered?.map(item => (
                  <ItemCard
                    key={item?.id}
                    item={item}
                    isAdmin={isAdmin}
                    onClick={() => {
                      setSelectedItem(item);
                      setAdminStatus(item?.status);
                      setAdminNotes(item?.admin_notes || '');
                      setMatchItemId(item?.matched_item_id || '');
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Recent Activity ── */}
        {activeTab === 'activity' && (
          <div className="bg-card border border-border rounded-xl p-6">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Activity" size={16} className="text-primary" />
              Recent Activity
            </h2>
            {recentActivity?.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Icon name="Activity" size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No activity yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity?.map(item => (
                  <div
                    key={item?.id}
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedItem(item);
                      setAdminStatus(item?.status);
                      setAdminNotes(item?.admin_notes || '');
                      setMatchItemId(item?.matched_item_id || '');
                    }}
                  >
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${item?.item_type === 'lost' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                      <Icon name={item?.item_type === 'lost' ? 'SearchX' : 'PackageSearch'} size={14} className={item?.item_type === 'lost' ? 'text-red-500' : 'text-green-500'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground truncate">{item?.item_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusStyle(item?.status)}`}>
                          {STATUSES?.find(s => s?.value === item?.status)?.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                        <span className="capitalize">{item?.item_type}</span>
                        {item?.building && <span className="flex items-center gap-1"><Icon name="Building2" size={10} />{item?.building}</span>}
                        <span>{new Date(item?.updated_at || item?.created_at)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                    {item?.photo_url && (
                      <img src={item?.photo_url} alt={item?.item_name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Matches Tab (Admin only) ── */}
        {activeTab === 'matches' && isAdmin && (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Link2" size={16} className="text-purple-500" />
                Potential Matches
                <span className="text-xs text-muted-foreground font-normal ml-1">— Items that may belong together</span>
              </h2>
              {(() => {
                const lostItems = items?.filter(i => i?.item_type === 'lost' && i?.status === 'open');
                const matchPairs = [];
                lostItems?.forEach(lostItem => {
                  const matches = getPotentialMatches(lostItem);
                  matches?.forEach(foundItem => {
                    const pairKey = [lostItem?.id, foundItem?.id]?.sort()?.join('-');
                    if (!matchPairs?.find(p => p?.key === pairKey)) {
                      matchPairs?.push({ key: pairKey, lost: lostItem, found: foundItem });
                    }
                  });
                });
                if (matchPairs?.length === 0) {
                  return (
                    <div className="text-center py-10 text-muted-foreground">
                      <Icon name="Link2" size={32} className="mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No potential matches found</p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-3">
                    {matchPairs?.map(pair => (
                      <div key={pair?.key} className="border border-border rounded-xl p-4 bg-muted/10">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 dark:bg-red-900/30 font-medium">Lost</span>
                              <span className="text-sm font-medium text-foreground">{pair?.lost?.item_name}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">{pair?.lost?.building || 'No building'} — {getCategoryLabel(pair?.lost?.category)}</div>
                          </div>
                          <div className="flex-shrink-0 text-purple-400">
                            <Icon name="ArrowLeftRight" size={18} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 dark:bg-green-900/30 font-medium">Found</span>
                              <span className="text-sm font-medium text-foreground">{pair?.found?.item_name}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">{pair?.found?.building || 'No building'} — {getCategoryLabel(pair?.found?.category)}</div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedItem(pair?.lost);
                              setAdminStatus('matched');
                              setMatchItemId(pair?.found?.id);
                              setAdminNotes(`Matched with found item: ${pair?.found?.item_name}`);
                            }}
                            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-medium hover:bg-purple-600 transition-colors"
                          >
                            <Icon name="Link2" size={12} />
                            Confirm Match
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* ── Item Detail Modal ── */}
      {selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          isAdmin={isAdmin}
          isViewer={isViewer}
          adminStatus={adminStatus}
          adminNotes={adminNotes}
          matchItemId={matchItemId}
          updatingItem={updatingItem}
          items={items}
          onClose={() => { setSelectedItem(null); setAdminNotes(''); setAdminStatus(''); setMatchItemId(''); }}
          onAdminStatusChange={setAdminStatus}
          onAdminNotesChange={setAdminNotes}
          onMatchItemChange={setMatchItemId}
          onSave={handleAdminUpdate}
          getPotentialMatches={getPotentialMatches}
        />
      )}
    </MainLayout>
  );
}

// ── Item Card Component ──────────────────────────────────────────────────────
function ItemCard({ item, isAdmin, onClick }) {
  return (
    <div
      className="bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer"
      onClick={onClick}
    >
      {item?.photo_url && (
        <div className="h-36 overflow-hidden bg-muted/30">
          <img src={item?.photo_url} alt={item?.item_name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
            item?.item_type === 'lost' ? 'bg-red-50 text-red-600 dark:bg-red-900/30' : 'bg-green-50 text-green-600 dark:bg-green-900/30'
          }`}>
            <Icon name={item?.item_type === 'lost' ? 'SearchX' : 'PackageSearch'} size={11} />
            {item?.item_type === 'lost' ? 'Lost' : 'Found'}
          </div>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusStyle(item?.status)}`}>
            {STATUSES?.find(s => s?.value === item?.status)?.label}
          </span>
        </div>
        <h3 className="font-semibold text-foreground text-sm mb-1 line-clamp-1">{item?.item_name}</h3>
        <div className="flex items-center gap-1.5 mb-2">
          <Icon name={getCategoryIcon(item?.category)} size={11} className="text-muted-foreground flex-shrink-0" />
          <span className="text-xs text-muted-foreground">{getCategoryLabel(item?.category)}</span>
        </div>
        {item?.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{item?.description}</p>}
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          {item?.building && (
            <div className="flex items-center gap-1">
              <Icon name="Building2" size={10} />
              <span>{item?.building}</span>
            </div>
          )}
          {item?.location && !item?.building && (
            <div className="flex items-center gap-1">
              <Icon name="MapPin" size={10} />
              <span className="truncate max-w-[100px]">{item?.location}</span>
            </div>
          )}
          <span>{new Date(item?.created_at)?.toLocaleDateString()}</span>
        </div>
        {isAdmin && item?.reporter?.full_name && (
          <div className="mt-2 pt-2 border-t border-border/50 flex items-center gap-1 text-xs text-muted-foreground">
            <Icon name="User" size={10} />
            <span>{item?.reporter?.full_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Item Detail Modal ────────────────────────────────────────────────────────
function ItemDetailModal({
  item, isAdmin, isViewer, adminStatus, adminNotes, matchItemId,
  updatingItem, items, onClose, onAdminStatusChange, onAdminNotesChange,
  onMatchItemChange, onSave, getPotentialMatches,
}) {
  const potentialMatches = isAdmin ? getPotentialMatches(item) : [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold mb-2 ${
                item?.item_type === 'lost' ? 'bg-red-50 text-red-600 dark:bg-red-900/30' : 'bg-green-50 text-green-600 dark:bg-green-900/30'
              }`}>
                <Icon name={item?.item_type === 'lost' ? 'SearchX' : 'PackageSearch'} size={12} />
                {item?.item_type === 'lost' ? 'Lost Item' : 'Found Item'}
              </div>
              <h2 className="text-lg font-bold text-foreground">{item?.item_name}</h2>
            </div>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted transition-colors">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>

          {/* Photo */}
          {item?.photo_url && (
            <div className="mb-4 rounded-xl overflow-hidden bg-muted/30 max-h-48">
              <img src={item?.photo_url} alt={item?.item_name} className="w-full h-full object-contain" />
            </div>
          )}

          {/* Details */}
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">Category</div>
                <div className="flex items-center gap-1.5 text-foreground">
                  <Icon name={getCategoryIcon(item?.category)} size={13} />
                  {getCategoryLabel(item?.category)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">Status</div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusStyle(item?.status)}`}>
                  {STATUSES?.find(s => s?.value === item?.status)?.label}
                </span>
              </div>
            </div>

            {item?.building && (
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">Building</div>
                <div className="flex items-center gap-1.5 text-foreground">
                  <Icon name="Building2" size={13} />
                  {item?.building}
                </div>
              </div>
            )}

            {item?.location && (
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">Location</div>
                <div className="flex items-center gap-1.5 text-foreground">
                  <Icon name="MapPin" size={13} />
                  {item?.location}
                </div>
              </div>
            )}

            {item?.description && (
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">Description</div>
                <div className="text-foreground">{item?.description}</div>
              </div>
            )}

            <div className="bg-muted/30 rounded-lg p-3">
              <div className="text-xs text-muted-foreground mb-1">Date Reported</div>
              <div className="text-foreground">{new Date(item?.created_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            </div>

            {item?.admin_notes && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                <div className="text-xs text-blue-600 dark:text-blue-400 mb-1 font-medium flex items-center gap-1">
                  <Icon name="MessageSquare" size={11} />
                  Admin Notes
                </div>
                <div className="text-foreground text-sm">{item?.admin_notes}</div>
              </div>
            )}

            {/* Potential matches shown to non-admin */}
            {!isAdmin && potentialMatches?.length === 0 && item?.status === 'matched' && (
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                <div className="text-xs text-purple-600 dark:text-purple-400 font-medium flex items-center gap-1">
                  <Icon name="Link2" size={11} />
                  This item has been matched! Please contact administration.
                </div>
              </div>
            )}
          </div>

          {/* Admin Actions */}
          {isAdmin && (
            <div className="mt-5 pt-5 border-t border-border space-y-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Settings" size={14} />
                Admin Actions
              </h3>

              {/* Status update */}
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5">Update Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {STATUSES?.filter(s => s?.value !== 'closed')?.map(s => (
                    <button
                      key={s?.value}
                      type="button"
                      onClick={() => onAdminStatusChange(s?.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                        adminStatus === s?.value
                          ? `${s?.color} border-current`
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      <Icon name={s?.icon} size={12} />
                      {s?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Potential matches for admin */}
              {potentialMatches?.length > 0 && (
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">
                    <span className="flex items-center gap-1">
                      <Icon name="Sparkles" size={11} className="text-amber-500" />
                      Potential Matches ({potentialMatches?.length})
                    </span>
                  </label>
                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                    {potentialMatches?.map(m => (
                      <button
                        key={m?.id}
                        type="button"
                        onClick={() => { onMatchItemChange(m?.id); onAdminStatusChange('matched'); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all text-left ${
                          matchItemId === m?.id
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' :'border-border text-muted-foreground hover:border-purple-400'
                        }`}
                      >
                        <Icon name={m?.item_type === 'found' ? 'PackageSearch' : 'SearchX'} size={12} />
                        <span className="font-medium">{m?.item_name}</span>
                        {m?.building && <span className="text-muted-foreground">— {m?.building}</span>}
                        {matchItemId === m?.id && <Icon name="Check" size={12} className="ml-auto text-purple-500" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual match ID */}
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5">Match with Item ID (manual)</label>
                <select
                  value={matchItemId}
                  onChange={e => onMatchItemChange(e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-xs focus:outline-none"
                >
                  <option value="">Select item to match...</option>
                  {items
                    ?.filter(i => i?.id !== item?.id && i?.item_type !== item?.item_type && i?.status === 'open')
                    ?.map(i => (
                      <option key={i?.id} value={i?.id}>
                        [{i?.item_type?.toUpperCase()}] {i?.item_name} {i?.building ? `— ${i?.building}` : ''}
                      </option>
                    ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs text-muted-foreground mb-1.5">Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={e => onAdminNotesChange(e?.target?.value)}
                  rows={3}
                  placeholder="Add notes for the resident..."
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none resize-none"
                />
              </div>

              <button
                onClick={onSave}
                disabled={updatingItem}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {updatingItem ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Save" size={14} />}
                {updatingItem ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
